/**
 * Runtime-portable subprocess spawning.
 *
 * `node:child_process` is implemented by both Node and Bun, so it is the
 * portable choice — no runtime detection needed (unlike embedded SQLite,
 * which has no shared API; see `logging/sinks/sqlite-runtime.ts`).
 *
 * These helpers wrap `node:child_process` to present a `Bun.Subprocess`-shaped
 * result — web `ReadableStream` stdio plus an `exited` promise — so call sites
 * migrated off `Bun.spawn` keep their stream-reading code unchanged.
 */
import { spawn, spawnSync } from "node:child_process";
import { constants } from "node:os";
import { Readable } from "node:stream";

export interface PortableSpawnOptions {
  cwd?: string;
  env?: Record<string, string | undefined>;
  detached?: boolean;
}

/**
 * Options for {@link portableSpawnSync}.
 *
 * `timeoutMs` is **required**, and deliberately so. A synchronous spawn blocks
 * the single-threaded event loop until the child exits; if the child stalls —
 * classically on a wedged network/FUSE mount reachable from `cwd` — the whole
 * process stops serving. An unbounded `spawnSync` is therefore not a call that
 * should be expressible, so the deadline is part of the type rather than an
 * optional extra a caller can forget.
 */
export interface PortableSpawnSyncOptions extends PortableSpawnOptions {
  /** Wall-clock limit for the child, in milliseconds. Must be > 0. */
  timeoutMs: number;
}

/** A spawned process with stdout/stderr piped. Mirrors the shape of `Bun.Subprocess`. */
export interface PortableSubprocess {
  /** OS process id, or -1 if the process failed to spawn. */
  readonly pid: number;
  readonly stdout: ReadableStream<Uint8Array>;
  readonly stderr: ReadableStream<Uint8Array>;
  /**
   * Resolves with the exit code once the process closes. Signal termination
   * uses the conventional `128 + signal number`; spawn errors or unknown
   * signals use `-1`.
   */
  readonly exited: Promise<number>;
  kill(signal?: number | NodeJS.Signals): void;
}

export interface PortableSpawnSyncResult {
  /** Exit code; -1 when the process did not exit cleanly. */
  exitCode: number;
  stdout: string;
  stderr: string;
  /**
   * `true` when the child was killed for exceeding
   * {@link PortableSpawnSyncOptions.timeoutMs}. Lets a caller tell "the command
   * failed" apart from "the command never finished", which are different
   * operational events.
   */
  timedOut: boolean;
}

/**
 * Spawn a subprocess with stdout/stderr piped. Works under Node and Bun.
 *
 * @param cmd - Command and arguments, e.g. `["docker", "ps"]`.
 */
export function portableSpawn(cmd: string[], opts: PortableSpawnOptions = {}): PortableSubprocess {
  const [bin, ...args] = cmd;
  if (!bin) throw new Error("portableSpawn: empty command");
  const child = spawn(bin, args, {
    cwd: opts.cwd,
    env: opts.env ?? process.env,
    stdio: ["ignore", "pipe", "pipe"],
    detached: opts.detached,
  });
  const exited = new Promise<number>((resolve) => {
    child.on("close", (code, signal) => {
      if (code !== null) {
        resolve(code);
        return;
      }
      const signalNumber = signal ? constants.signals[signal] : undefined;
      resolve(signalNumber === undefined ? -1 : 128 + signalNumber);
    });
    child.on("error", () => resolve(-1));
  });
  return {
    pid: child.pid ?? -1,
    stdout: Readable.toWeb(child.stdout as Readable) as unknown as ReadableStream<Uint8Array>,
    stderr: Readable.toWeb(child.stderr as Readable) as unknown as ReadableStream<Uint8Array>,
    exited,
    kill: (signal) => {
      if (opts.detached && child.pid) {
        try {
          process.kill(-child.pid, signal);
          return;
        } catch {
          /* fall back to killing the direct child */
        }
      }
      child.kill(signal);
    },
  };
}

/**
 * Run a subprocess to completion synchronously, capturing stdout/stderr as
 * UTF-8 strings. Works under Node and Bun.
 *
 * Blocks the event loop for the child's whole lifetime, so `opts.timeoutMs`
 * is required — see {@link PortableSpawnSyncOptions}. Prefer
 * {@link portableSpawn} wherever an async call is possible; only reach for this
 * when the caller genuinely cannot await (process teardown, CLI entry points).
 *
 * The child is killed with `SIGKILL` rather than `SIGTERM` on expiry: a process
 * blocked in an uninterruptible kernel wait (`D` state, e.g. a dead FUSE
 * connection) never runs a `SIGTERM` handler, so a graceful signal would leave
 * the deadline unenforced.
 *
 * @param cmd - Command and arguments, e.g. `["docker", "info"]`.
 * @param opts - Spawn options; `timeoutMs` is mandatory.
 */
export function portableSpawnSync(
  cmd: string[],
  opts: PortableSpawnSyncOptions,
): PortableSpawnSyncResult {
  const [bin, ...args] = cmd;
  if (!bin) throw new Error("portableSpawnSync: empty command");
  if (!(opts.timeoutMs > 0)) {
    throw new Error(`portableSpawnSync: timeoutMs must be > 0 (got ${opts.timeoutMs})`);
  }
  const result = spawnSync(bin, args, {
    cwd: opts.cwd,
    env: opts.env ?? process.env,
    encoding: "utf8",
    timeout: opts.timeoutMs,
    killSignal: "SIGKILL",
  });
  // Node reports the expiry as `error.code === "ETIMEDOUT"`; Bun surfaces the
  // same case as a SIGKILL with no status, so accept either signal.
  const timedOut =
    (result.error as NodeJS.ErrnoException | undefined)?.code === "ETIMEDOUT" ||
    (result.status === null && result.signal === "SIGKILL");
  let stderr = result.stderr ?? "";
  if (timedOut) {
    // Surface the reason on the channel every caller already inspects — an
    // exitCode of -1 alone reads as "command failed" and hides the stall.
    stderr += `${stderr.endsWith("\n") || stderr === "" ? "" : "\n"}${bin}: timed out after ${opts.timeoutMs}ms\n`;
  }
  return {
    exitCode: result.status ?? -1,
    stdout: result.stdout ?? "",
    stderr,
    timedOut,
  };
}
